// @ts-nocheck

import React, {
  ReactElement,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import Form from 'octa-front-sdk/dist/components/Form';
import Input from 'octa-front-sdk/dist/components/Input';
import {
  maskCardNumber,
  validateCardNumber,
  maskExpirationDate,
  validateExpirationDate,
  validateCVV,
  maskMoney,
  validateRequired,
} from 'octa-front-sdk';
import Checkbox from 'octa-front-sdk/dist/components/Checkbox';
import Button from 'octa-front-sdk/dist/components/Button';
import Color from 'octa-front-sdk/dist/enums/Color';
import RequestHandler from 'octa-front-sdk/dist/RequestHandler';
import ButtonType from 'octa-front-sdk/dist/enums/ButtonType';
import InputType from 'octa-front-sdk/dist/enums/InputType';
import OrganizationContext from '../../../../contexts/OrganizationContext';
import { formatPrice } from '../../../../Helpers';
import CouponType from '../../../../enums/CouponType';
import { DiscountCoupon } from '../../../../types/DiscountCoupon';
import PaymentOptionsStyles from './styles';
import { OrderPaymentCardRequest } from '../../../../types/OrderPaymentCardRequest';
import { OrderOnlinePaymentRequest } from '../../../../types/OrderOnlinePaymentRequest';
import { CardInfo } from '../../../../types/CardInfo';
import Zoop from '../../../../services/Zoop';
import GlobalContext from '../../../../contexts/GlobalContext';

interface IProps {
  couponCode: string;
  setCouponCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDiscount: React.Dispatch<React.SetStateAction<DiscountCoupon | undefined>>;
  deliveryFee: number;
  cartTotal: number;
  chosenMethod: string;
  setChosenMethod: React.Dispatch<React.SetStateAction<string | undefined>>;
  valueForChange: string;
  setValueForChange: React.Dispatch<React.SetStateAction<string>>;
  placeOrder: (onlinePayment?: OrderOnlinePaymentRequest | undefined) => void;
  isSendingOrder: boolean;
  setIsSendingOrder: React.Dispatch<boolean | undefined>;
}

const PaymentOptions: React.FC<IProps> = (props): ReactElement => {
  const {
    couponCode,
    setCouponCode,
    setDiscount,
    deliveryFee,
    chosenMethod,
    setChosenMethod,
    valueForChange,
    setValueForChange,
    placeOrder,
    isSendingOrder,
    setIsSendingOrder,
  } = props;

  const { organizationInfo } = useContext(OrganizationContext);
  const { guestUUID, cartItems, setPaymentModifier } = useContext(
    GlobalContext,
  );

  const [currentTab, setCurrentTab] = useState(0);
  const [shouldRenderCouponSection, setShouldRenderCouponSection] = useState();
  const [isCheckingCoupon, setIsCheckingCoupon] = useState();
  const [couponMessage, setCouponMessage] = useState();
  const [isCodeValid, setIsCodeValid] = useState();
  const [shouldRenderTabs, setShouldRenderTabs] = useState();
  const [isLoadingCard, setIsLoadingCard] = useState();
  const [cardInfo, setCardInfo] = useState();
  const [shouldSaveCreditCard, setShouldSaveCreditCard] = useState(false);
  const [userPayment, setUserPayment] = useState();
  const [shouldUseSavedCard, setShouldUseSavedCard] = useState();

  const tabs = ['Pagar pelo app', 'Outras formas de pagamento'];

  const getCartTotalForCupom = useCallback((): number => {
    return cartItems.reduce((sum, current): number => {
      if (!current.product.forbidsDiscountCoupon)
        return (sum += current.totalPrice);
      return sum;
    }, 0);
  }, [cartItems]);

  const checkAndApplyCoupon = useCallback((): void => {
    setIsCheckingCoupon(true);

    RequestHandler.get(`coupons/valid?code=${couponCode}`)
      .then((response): void => {
        const discount: DiscountCoupon = response.data;
        const totalValideForDiscount = getCartTotalForCupom();

        if (totalValideForDiscount === 0)
          return setCouponMessage(
            <>Nenhum item do carrinho é válido para cupom!</>,
          );

        if (
          discount.minOrderValue &&
          totalValideForDiscount + deliveryFee < discount.minOrderValue
        ) {
          setIsCodeValid(false);

          setCouponMessage(
            <>
              Válido apenas para compras a partir de{' '}
              <strong>{formatPrice(discount.minOrderValue)}</strong>.
            </>,
          );
        } else {
          setIsCodeValid(true);
          setDiscount(discount);

          setCouponMessage(
            <>
              Você ganhou{' '}
              {!!discount.discount && (
                <>
                  <strong>
                    {discount.type === CouponType.Value
                      ? `${formatPrice(discount.discount)}`
                      : `${discount.discount * 100}%`}

                    {!!discount.maxDiscountValue &&
                      ` (limite de ${formatPrice(discount.maxDiscountValue)})`}
                  </strong>{' '}
                  de desconto
                </>
              )}
              {!!discount.discount && discount.freeDelivery && ' e '}
              {discount.freeDelivery && <strong>frete grátis</strong>}.
            </>,
          );
        }
      })
      .catch((): void => {
        setIsCodeValid(false);
        setCouponMessage('Código inválido.');
      })
      .finally((): void => setIsCheckingCoupon(false));
  }, [couponCode, deliveryFee, setDiscount, getCartTotalForCupom]);

  const renderPaymentModifier = useCallback((modifier, type): string => {
    const typeText = modifier < 0 ? 'Desconto' : 'Taxa';
    modifier = Math.abs(modifier);

    return `${typeText} de ${
      type === 'Percent' ? `${modifier * 100}%` : formatPrice(modifier)
    }`;
  }, []);

  const renderContent = useCallback((): ReactElement => {
    const onlinePaymentContent =
      userPayment && shouldUseSavedCard ? (
        <div>
          {organizationInfo &&
            !!organizationInfo.paymentConfiguration &&
            !!organizationInfo.paymentConfiguration.paymentModifier && (
              <p>
                <strong>
                  {organizationInfo.paymentConfiguration.paymentModifier
                    .modifier < 0
                    ? 'Desconto: '
                    : 'Taxa: '}
                </strong>
                {formatPrice(
                  organizationInfo.paymentConfiguration.paymentModifier
                    .modifier,
                )}
              </p>
            )}

          <p>
            <strong>Bandeira: </strong> {userPayment.brand}
          </p>

          <p>
            <strong>Cartão: </strong> {`**** ${userPayment.truncatedNumber}`}
          </p>

          <PaymentOptionsStyles.TextButton
            type="button"
            onClick={(): void => setShouldUseSavedCard(false)}
          >
            Informar outro cartão
          </PaymentOptionsStyles.TextButton>
        </div>
      ) : (
        <>
          <Input
            name="holderName"
            label="Nome do titular"
            material
            validator={validateRequired}
            onChange={(e): void =>
              setCardInfo({
                ...cardInfo,
                holderName: e.target.value.toString(),
              })
            }
          />

          <Input
            name="cardNumber"
            label="Número do cartão"
            material
            mask={maskCardNumber}
            validator={validateCardNumber}
            onChange={(e): void =>
              setCardInfo({
                ...cardInfo,
                cardNumber: e.target.value.toString(),
              })
            }
          />

          <PaymentOptionsStyles.InputsWrapper>
            <Input
              name="expirationDate"
              label="Validade"
              material
              mask={maskExpirationDate}
              validator={validateExpirationDate}
              onChange={(e): void =>
                setCardInfo({
                  ...cardInfo,
                  expirationDate: e.target.value.toString(),
                })
              }
            />

            <Input
              name="cvv"
              label="CVV"
              material
              validator={validateCVV}
              onChange={(e): void =>
                setCardInfo({ ...cardInfo, cvv: e.target.value.toString() })
              }
            />
          </PaymentOptionsStyles.InputsWrapper>

          {!guestUUID && (
            <PaymentOptionsStyles.CheckboxWrapper
              onClick={(): void =>
                setShouldSaveCreditCard(!shouldSaveCreditCard)
              }
            >
              <Checkbox
                name="saveCard"
                checked={shouldSaveCreditCard}
                changeHandler={(): void => {}}
              />
              Salvar cartão para compras futuras
            </PaymentOptionsStyles.CheckboxWrapper>
          )}
        </>
      );

    const deliveryPaymentContent = (
      <>
        <PaymentOptionsStyles.PaymentMethods>
          {organizationInfo &&
            organizationInfo.paymentMethods.map(
              (method): ReactElement => {
                const { id, name, paymentModifier } = method;

                return (
                  <PaymentOptionsStyles.PaymentMethod key={id}>
                    <Input
                      key={id}
                      name="chosenMethod"
                      type={InputType.Radio}
                      value={id.toString()}
                      onChange={(e): void => {
                        setPaymentModifier(paymentModifier);
                        setChosenMethod(e.target.value);
                      }}
                      label={`${name}`}
                    />
                    {!!paymentModifier && (
                      <PaymentOptionsStyles.PaymentModifierText
                        isDiscount={paymentModifier.modifier < 0}
                      >
                        {renderPaymentModifier(
                          paymentModifier.modifier,
                          paymentModifier.modifierType,
                        )}
                      </PaymentOptionsStyles.PaymentModifierText>
                    )}
                  </PaymentOptionsStyles.PaymentMethod>
                );
              },
            )}
        </PaymentOptionsStyles.PaymentMethods>

        {organizationInfo &&
          organizationInfo.paymentMethods.find(
            (method): boolean =>
              method.name.toLocaleLowerCase() === 'dinheiro' &&
              method.id === parseInt(chosenMethod),
          ) && (
            <PaymentOptionsStyles.ChangeContainer>
              <Input
                name="valueForChange"
                label="Troco para:"
                mask={maskMoney}
                onChange={(e): void => {
                  const { value } = e.target;

                  setValueForChange(value || '0');
                }}
              />
            </PaymentOptionsStyles.ChangeContainer>
          )}

        {organizationInfo && organizationInfo.paymentObservation && (
          <PaymentOptionsStyles.Observation>
            Obs: {organizationInfo.paymentObservation}
          </PaymentOptionsStyles.Observation>
        )}
      </>
    );

    const contentOptions = [onlinePaymentContent, deliveryPaymentContent];

    return contentOptions[currentTab];
  }, [
    chosenMethod,
    currentTab,
    organizationInfo,
    setChosenMethod,
    setValueForChange,
    cardInfo,
    shouldSaveCreditCard,
    userPayment,
    shouldUseSavedCard,
    guestUUID,
    renderPaymentModifier,
    setPaymentModifier,
  ]);

  const tokenizeCard = useCallback(
    (cardInfo: CardInfo): Promise<OrderPaymentCardRequest> =>
      Zoop.tokenizeCard(cardInfo),
    [],
  );

  useEffect((): void => {
    if (organizationInfo && organizationInfo.hasOnlinePayment) {
      setShouldRenderTabs(true);

      setIsLoadingCard(true);

      RequestHandler.get('user/payment')
        .then((response): void => {
          const userPayment = response.data;

          if (userPayment) {
            setUserPayment(userPayment);
            setShouldUseSavedCard(true);
          }
        })
        .finally((): void => setIsLoadingCard(false));
    } else {
      setCurrentTab(1);
    }
  }, [organizationInfo]);

  useEffect((): void => {
    if (shouldRenderCouponSection) {
      const input = document.getElementById('couponCode');

      if (input) {
        input.addEventListener('keydown', (e): void => {
          e.key === 'Enter' && e.preventDefault();
        });
      }
    }
  }, [shouldRenderCouponSection]);

  useEffect((): void => {
    if (currentTab === 0) {
      if (
        organizationInfo &&
        organizationInfo.paymentConfiguration &&
        organizationInfo.paymentConfiguration.paymentModifier
      ) {
        const onlinePaymentModifier = {
          id: organizationInfo.paymentConfiguration.paymentModifier.id,
          modifier:
            organizationInfo.paymentConfiguration.paymentModifier.modifier,
          modifierType:
            organizationInfo.paymentConfiguration.paymentModifier.modifierType,
        };

        setPaymentModifier(onlinePaymentModifier);
      } else {
        setPaymentModifier(undefined);
      }
      setChosenMethod(undefined);
    }
  }, [currentTab, setChosenMethod, organizationInfo, setPaymentModifier]);

  return (
    <PaymentOptionsStyles.Container>
      <Form
        key={`${currentTab}-${shouldUseSavedCard}`}
        onSubmit={(values): void => {
          if (currentTab === 0) {
            if (shouldUseSavedCard) {
              placeOrder({ useSavedCard: true });
            } else {
              const { holderName, cardNumber, expirationDate, cvv } = values;
              const cardInfo: CardInfo = {
                holderName: holderName.toString(),
                cardNumber: cardNumber.toString(),
                expirationDate: expirationDate.toString(),
                cvv: cvv.toString(),
              };

              setIsSendingOrder(true);

              tokenizeCard(cardInfo)
                .then((userPayment): void => {
                  setIsSendingOrder(false);

                  if (shouldSaveCreditCard) {
                    RequestHandler.put('user/payment', userPayment).then(
                      (): void => {
                        placeOrder({ useSavedCard: true });
                      },
                    );
                  } else {
                    placeOrder({
                      useSavedCard: false,
                      card: { ...userPayment },
                    });
                  }
                })
                .catch((): void => {
                  setIsSendingOrder(false);
                });
            }
          } else {
            placeOrder();
          }
        }}
        initialValues={{
          ...cardInfo,
          couponCode,
          chosenMethod,
          valueForChange,
        }}
      >
        <PaymentOptionsStyles.Card>
          {shouldRenderTabs && (
            <PaymentOptionsStyles.TabButtonsWrapper>
              {tabs.map(
                (t, i): ReactElement => {
                  const className = currentTab === i ? 'active' : '';

                  return (
                    <PaymentOptionsStyles.TabButton
                      key={i}
                      className={className}
                      onClick={(): void => setCurrentTab(i)}
                      type="button"
                    >
                      {t}
                    </PaymentOptionsStyles.TabButton>
                  );
                },
              )}
            </PaymentOptionsStyles.TabButtonsWrapper>
          )}

          <PaymentOptionsStyles.Content>
            {isLoadingCard ? (
              <p>Verificando cartões salvos...</p>
            ) : (
              renderContent()
            )}
          </PaymentOptionsStyles.Content>
        </PaymentOptionsStyles.Card>

        {shouldRenderCouponSection ? (
          <PaymentOptionsStyles.CouponSection>
            <>
              <Input
                label="Código"
                name="couponCode"
                onChange={(e): void => setCouponCode(e.target.value)}
                material
              />

              <Button
                text={isCheckingCoupon ? 'Validando cupom...' : 'Validar cupom'}
                color={Color.MeuDeliveryCyan}
                isSubmit={false}
                type={ButtonType.Outline}
                onClick={checkAndApplyCoupon}
              />
            </>

            {couponMessage && (
              <PaymentOptionsStyles.CouponMessage
                className={isCodeValid ? 'valid' : 'invalid'}
              >
                {couponMessage}
              </PaymentOptionsStyles.CouponMessage>
            )}

            <PaymentOptionsStyles.TextButton
              type="button"
              onClick={(): void => {
                setCouponCode(undefined);
                setDiscount(undefined);
                setCouponMessage(undefined);
                setShouldRenderCouponSection(false);
              }}
            >
              Remover cupom
            </PaymentOptionsStyles.TextButton>
          </PaymentOptionsStyles.CouponSection>
        ) : (
          <PaymentOptionsStyles.TextButton
            type="button"
            onClick={(): void => setShouldRenderCouponSection(true)}
          >
            Adicionar cupom
          </PaymentOptionsStyles.TextButton>
        )}

        <Button
          text={isSendingOrder ? 'Enviando...' : 'Realizar pedido'}
          isDisabled={isSendingOrder}
          color={Color.MeuDeliveryCyan}
        />
      </Form>
    </PaymentOptionsStyles.Container>
  );
};

export default PaymentOptions;
